import type { CreateAssetTransferPayload } from '@/store/slices/assetTransferSlice';
import api, { apiFormData } from './api';
import { UserDropDown } from '@/types/user.types';
import type { Asset } from '@/store/api/assetApi';

// Types
export interface LocationDetail {
  id: string;
  name: string;
  site?: string;
  site_detail?: Record<string, unknown>;
  location_type?: string;
  description?: string;
  parent_location?: string;
  parent_location_detail?: Record<string, unknown>;
  location_hierarchy?: string;
  // is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  child_count?: number;
  asset_count?: number;
}

export interface AssetDetail {
  serial_number: string;
  manufacturer_detail: any;
  model_detail: any;
  purchase_value: any;
  id: string;
  asset_tag?: string;
  asset_name?: string;
  company?: string;
  description?: string;
  asset_category?: string;
  asset_category_detail?: Record<string, unknown>;
  asset_status?: string;
  asset_status_detail?: Record<string, unknown>;
  location?: string;
  location_detail?: Record<string, unknown>;
  department?: string;
  department_detail?: Record<string, unknown>;
  assigned_to?: string;
  assigned_to_detail?: Record<string, unknown>;
}

export interface DocumentDetail {
  id: string;
  content_type: number;
  object_id: string;
  file_type: string;
  filename: string;
  file_size: number;
  checksum_md5: string;
  status: string;
  created_at: string;
  updated_at: string;
  uploaded_by: string;
}

interface Department {
  id: string;
  name: string;
  code?: string;
  description?: string;
}

interface UserDetail {
  id: string;
  name: string;
  email: string;
}

export interface AssetTransferRequest {
  asset: string | undefined;
  documents_detail: DocumentDetail[];
  created_by_detail: UserDropDown;
  workflow_instance_detail: any;
  current_custodian_detail: UserDetail;
  current_department_detail: Department;
  target_department_detail: Department;
  target_custodian_detail: UserDetail;
  id?: string;
  asset_ids: string[];
  assets_detail?: AssetDetail[];
  reason: string;
  from_location: string;
  to_location: string;
  from_location_detail?: LocationDetail;
  to_location_detail?: LocationDetail;
  comments?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  documents?: AssetTransferDocument[];
  current_custodian: string;
  target_custodian: string;
  current_department: string;
  target_department: string;
  transfer_cost?: string;
  vehicle?: string | null;
  vehicle_detail?: any;
  tracking_type?: string;
  custom_fields?: Record<string, any>;
  custom_fields_detail?: Record<string, any>;
  transfer_number?: string;
  updated_by_detail?: UserDetail;
  movement_tracking_enabled?: boolean;
  departure_timestamp?: string;
  arrival_timestamp?: string;
  checkout_timestamp?: string;
  checkin_timestamp?: string;
  checkout_reader_id?: string;
  checkin_reader_id?: string;
  details?: {
    id: string;
    asset: string;
    asset_detail?: AssetDetail & {
      asset_code?: string;
      asset_name?: string;
      asset_tag?: string;
      serial_number?: string;
      category?: string;
      asset_category_detail?: { name?: string };
      location_detail?: { name?: string; location_hierarchy?: string };
      purchase_date?: string;
      purchase_value?: string;
      current_value?: string;
    };
    from_location?: string;
    from_location_detail?: LocationDetail;
    current_custodian?: string;
    current_custodian_detail?: UserDropDown;
    current_department?: string;
    current_department_detail?: Department;
  }[];
}

interface TransferRequestDetail {
  id: string;
  reason: string;
  reason_display: string;
  to_location: string;
  to_location_detail: LocationDetail;
  target_custodian?: string;
  target_custodian_detail?: UserDetail;
  target_department?: string;
  target_department_detail?: Department;
  transfer_cost?: string;
  status: string;
  status_display: string;
  comments?: string;
  created_at: string;
  created_by: string;
  created_by_detail: UserDetail;
  completed_at?: string;
}

export interface TransferRequest extends AssetTransferRequest {
  asset_detail?: Asset;
  from_location_detail?: LocationDetail;
  current_custodian_detail: UserDetail;
  current_department_detail: Department;
  transfer_request_detail?: TransferRequestDetail;
  id: string;
  transfer_request: string;
};

export interface AssetTransferDocument {
  id?: string;
  transfer_request: string;
  file: File | string;
  file_name?: string;
  file_type?: string;
  file_size?: number;
  uploaded_at?: string;
}

export interface AssetSearchResult {
  id: string;
  code: string;
  name: string;
  category: string;
  location: string;
  department: string;
  department_id?: string;
  custodian: string;
  custodian_id?: string;
}

// API Service
const assetTransferService = {
  // Get a list of transfer requests with optional filtering and pagination
  getTransferRequests: async (params?: {
    page?: number;
    page_size?: number;
    limit?: number;
    status?: string;
    created_at_after?: string;
    created_at_before?: string;
    search?: string;
    ordering?: string;
    asset_tags?: string;
    to_locations?: string;
    from_locations?: string;
    created_by?: string;
    current_departments?: string;
    target_departments?: string;
    current_custodians?: string;
    target_custodians?: string;
    manufacturers?: string;
    models?: string;
    serial_numbers?: string;
  }) => {
    try {
      const response = await api.get(
        "assets/transfers/requests/detail/",
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching transfer requests:', error);
      throw error;
    }
  },

  // Get details of a specific transfer request
  getTransferRequestById: async (id: string) => {
    try {
      const response = await api.get(
        `assets/transfers/requests/${id}/`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching transfer request with ID ${id}:`, error);
      throw error;
    }
  },

  // Create a new transfer request
  createTransferRequest: async (data: CreateAssetTransferPayload) => {
    try {
      const formData = new FormData();

      // Required fields
      formData.append('reason', data.reason);
      formData.append('to_location', data.to_location);
      formData.append('target_custodian', data.target_custodian);
      formData.append('target_department', data.target_department);
      formData.append('transfer_cost', String(data.transfer_cost));
      formData.append('comments', data.comments || '');

      data.asset_ids.forEach((id) => {
        formData.append('asset_ids', id);
      });

      // Movement tracking
      formData.append(
        'movement_tracking_enabled',
        String(data.movement_tracking_enabled)
      );

      if (data.tracking_type) {
        formData.append('tracking_type', data.tracking_type);
      }

      // Vehicle
      if (data.vehicle) {
        formData.append('vehicle', data.vehicle);
      }

      // Custom fields
      if (data.custom_fields && Object.keys(data.custom_fields).length > 0) {
        formData.append('custom_fields', JSON.stringify(data.custom_fields));
      }

      if (data.files && data.files.length > 0) {
        data.files.forEach((file) => {
          formData.append('documents', file);
        });
      }

      console.log('FormData payload:');
      console.log(formData);
      for (const pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      const response = await apiFormData.post(
        '/assets/transfers/requests/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error creating transfer request:', error);
      throw error;
    }
  },

  // Update an existing transfer request
  updateTransferRequest: async (
    id: string,
    data: Partial<AssetTransferRequest>
  ) => {
    try {
      const response = await api.patch(
        `assets/transfers/requests/${id}/submit`,
        data
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating transfer request with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete a transfer request
  deleteTransferRequest: async (id: string) => {
    try {
      await api.delete(`assets/transfers/requests/${id}/`);
      return true;
    } catch (error) {
      console.error(`Error deleting transfer request with ID ${id}:`, error);
      throw error;
    }
  },

  // Upload a document for a transfer request
  uploadTransferDocument: async (transferId: string, file: File) => {
    try {
      const formData = new FormData();
      formData.append('transfer_request', transferId);
      formData.append('file', file);

      const response = await apiFormData.post(
        "assets/transfers/documents/",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading transfer document:', error);
      throw error;
    }
  },

  // Get documents for a transfer request
  getTransferDocuments: async (transferId: string) => {
    try {
      const response = await api.get(
        "assets/transfers/documents/",
        {
          params: { transfer_request: transferId },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching documents for transfer request with ID ${transferId}:`,
        error
      );
      throw error;
    }
  },

  // Create a bulk transfer request
  createBulkTransferRequest: async (
    assetIds: string[],
    transferData: Omit<AssetTransferRequest, 'asset'>
  ) => {
    try {
      const data = {
        ...transferData,
        assets: assetIds,
      };
      const response = await api.post(
        "assets/transfers/",
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating bulk transfer request:', error);
      throw error;
    }
  },

  // Get transfer history for an asset
  getAssetTransferHistory: async (assetId: string) => {
    try {
      const response = await api.get("assets/transfers/history/", {
        params: { asset: assetId },
      });
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching transfer history for asset with ID ${assetId}:`,
        error
      );
      throw error;
    }
  },

  // Search assets for transfer
  searchAssets: async (
    query: string,
    categoryId?: string | string[],
    locationId?: string | string[],
    departmentId?: string | string[]
  ) => {
    try {
      const params: Record<string, string> = {};
      
      if (query) {
        params.search = query;
      }
      
      if (categoryId) {
        if (Array.isArray(categoryId)) {
          params.asset_category = categoryId.join(',');
        } else {
          params.asset_category = categoryId;
        }
      }
      
      if (locationId) {
        if (Array.isArray(locationId)) {
          params.location = locationId.join(',');
        } else {
          params.location = locationId;
        }
      }
      
      if (departmentId) {
        if (Array.isArray(departmentId)) {
          params.department = departmentId.join(',');
        } else {
          params.department = departmentId;
        }
      }

      const response = await api.get('assets/dropdown/', { params });
      
      // Transform the response to match AssetSearchResult interface
      return response.data.results?.map((asset: any) => ({
        id: asset.id,
        code: asset.asset_tag || '',
        name: asset.asset_name || '',
        category: asset.asset_category_detail?.name || '',
        location: asset.location_detail?.name || '',
        department: asset.department_detail?.name || '',
        department_id: asset.department || '',
        custodian: asset.assigned_to_detail?.name || '',
        custodian_id: asset.assigned_to || '',
      })) || [];
    } catch (error) {
      console.error('Error searching assets:', error);
      throw error;
    }
  },
};

export default assetTransferService;
